import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { ReactComponent as ArrowDown } from 'src/Images/icons/common/arrowdown.svg';

function Table({
  headers = [],
  minCellWidth,
  tableContent,
  handleTableSortOrder,
}) {
  const authReducer = useSelector((state) => state.authReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const [tableHeight, setTableHeight] = useState('auto');
  const [colSelectedForResizeIdx, setColSelectedForResizeIdx] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortOrder: null,
    sortColumn: null,
  });
  const tableElement = useRef(null);
  const columnRefs = useRef(headers.map(() => React.createRef()));

  useEffect(() => {
    columnRefs.current = headers.map(() => React.createRef());
  }, [headers]);

  useEffect(() => {
    if (tableElement.current) {
      setTableHeight(tableElement.current.offsetHeight);
    }
  }, []);

  const handleColumnResize = useCallback(
    (e) => {
      if (
        colSelectedForResizeIdx === null ||
        !columnRefs.current[colSelectedForResizeIdx]
      )
        return;

      const gridColumns = headers.map((_, i) => {
        const columnRef = columnRefs.current[i]?.current;

        if (i === colSelectedForResizeIdx && columnRef) {
          const width = e.clientX - columnRef.getBoundingClientRect().left;

          if (width >= minCellWidth) {
            return `${width}px`;
          }
        }

        return columnRef ? `${columnRef.offsetWidth}px` : 'auto';
      });

      tableElement.current.style.gridTemplateColumns = `${gridColumns.join(' ')}`;
    },
    [colSelectedForResizeIdx, headers, minCellWidth],
  );

  const handleTouchResize = useCallback(
    (e) => {
      if (!e.touches || e.touches.length === 0) return;

      const touch = e.touches[0];
      handleColumnResize({ clientX: touch.clientX });
    },
    [handleColumnResize],
  );

  const cleanupResizeListeners = useCallback(() => {
    window.removeEventListener('mousemove', handleColumnResize);
    window.removeEventListener('mouseup', endColumnResize);
    window.removeEventListener('touchmove', handleTouchResize);
    window.removeEventListener('touchend', endColumnResize);
  }, [handleColumnResize]);

  const endColumnResize = useCallback(() => {
    setColSelectedForResizeIdx(null);
    cleanupResizeListeners();
  }, [cleanupResizeListeners]);

  useEffect(() => {
    if (colSelectedForResizeIdx !== null) {
      window.addEventListener('mousemove', handleColumnResize);
      window.addEventListener('mouseup', endColumnResize);
      window.addEventListener('touchmove', handleTouchResize);
      window.addEventListener('touchend', endColumnResize);
    }

    return () => {
      cleanupResizeListeners();
    };
  }, [
    colSelectedForResizeIdx,
    handleColumnResize,
    endColumnResize,
    cleanupResizeListeners,
  ]);

  const handleSort = (columnIndex) => {
    let newSortOrder = null;

    if (sortConfig.sortColumn === columnIndex) {
      newSortOrder =
        sortConfig.sortOrder === null
          ? 2
          : sortConfig.sortOrder === 2
            ? 1
            : null;
    } else {
      newSortOrder = 2;
    }

    setSortConfig({ sortOrder: newSortOrder, sortColumn: columnIndex });
    handleTableSortOrder(newSortOrder, columnIndex + 1);
  };

  const checkButtonShow = (i) =>
    sortConfig.sortColumn === i && sortConfig.sortOrder !== null;

  return (
    <Container fluid className="m-0 py-2 px-0">
      <div className={`theme-scrollable-container-${themeColor}`}>
        <div className="sync-event-table-wrapper">
          <table
            className="custom-table"
            style={{
              gridTemplateColumns: `repeat(${headers.length}, minmax(${minCellWidth || 100}px, 1fr))`,
              minWidth: `${headers.length * (minCellWidth || 100)}px`,
            }}
            ref={tableElement}
          >
            <thead className="table-head-container">
              <tr>
                {headers.map((headerObj, idx) => (
                  <th
                    className="table-header"
                    ref={columnRefs.current[idx]}
                    key={`header-${headerObj.label}`}
                  >
                    <button
                      className="icon-btn-none"
                      onClick={() => handleSort(idx)}
                    >
                      <div className="div-flex-row cg-10 div-align-center">
                        {idx === 0 ? (
                          <>
                            <input type="checkbox" className="checkbox-grey" />
                            <span className="text-sm text-grey">
                              {headerObj.label}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-grey">
                            {headerObj.label}
                          </span>
                        )}
                        {checkButtonShow(idx) && (
                          <ArrowDown
                            className={`icon-size-sm ${
                              sortConfig.sortOrder === 1
                                ? 'rotate-icon-180'
                                : ''
                            }`}
                            color="#9F9F9F"
                          />
                        )}
                      </div>
                    </button>
                    <button
                      style={{ height: tableHeight }}
                      onMouseDown={() => setColSelectedForResizeIdx(idx)}
                      onTouchStart={() => setColSelectedForResizeIdx(idx)}
                      className={`resize-handle ${
                        colSelectedForResizeIdx === idx ? 'active' : 'idle'
                      }`}
                      aria-label={`Resize column ${headerObj.label}`}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            {tableContent}
          </table>
        </div>
      </div>
    </Container>
  );
}

export default Table;
